import { queryDeviceLocations } from '@/api/device-locations'
import { createRequestCancellation } from '@/api/http'
import { chartHost } from '@/runtime/device-chart-runtime'
import { isStaticDataPreview, sharedReadOptions, readonlyDeviceStream } from '@/runtime/shared-view'
import { locationQuery, MAP_LOCATION_TOPIC } from '@/runtime/device-map-model'
import { createLocationFeed } from '@/runtime/device-map-feed'
import { registerMapDiagnostic } from '@/runtime/device-map-status'

export default {
  data: () => ({ locationRows: [], locationStatus: '', locationError: '' }),
  computed: {
    locationContentHidden () {
      if (Number(this.dataType) !== 5 || this.option.hideWhenEmpty !== true || this.locationStatus !== 'ready' || this.locationRows.length) return false
      const host = chartHost(this)
      return !(host.menuFlag && !host.isPreview) && !isStaticDataPreview(host) && !host.isTemplateEditing && !host.isComponentTemplateEditing && !host.isTemplatePreview
    },
    locationBinding () {
      const host = chartHost(this)
      return JSON.stringify([this.dataType, this.parameter, host.navigationContext && host.navigationContext.productId, host.navigationContext && host.navigationContext.deviceId, !!host.menuFlag, !!host.isPreview,
        !!host.isSharedView, !!host.isSharedDataView, isStaticDataPreview(host)])
    }
  },
  watch: { locationBinding () { if (this._locationMounted) this.updateData() } },
  mounted () { this._locationMounted = true; if (Number(this.dataType) === 5) this.updateLocationFeed() },
  beforeDestroy () { this._locationMounted = false; this.stopLocationFeed(); if (this._mapDiagnostic) this._mapDiagnostic.close() },
  methods: {
    stopLocationFeed () {
      clearTimeout(this._locationRetry)
      this._locationSequence = (this._locationSequence || 0) + 1
      if (this._locationFeed) this._locationFeed.close()
      if (this._locationRequest) this._locationRequest.cancel()
      this._locationFeed = null; this._locationRequest = null; this._locationKey = ''
    },
    retryLocations () { this._locationAttempts = 0; this.updateLocationFeed(true) },
    updateLocationFeed (reconnect = false) {
      if (!this._locationMounted) return
      if (!reconnect && this._locationKey === this.locationBinding && this._locationFeed) { this.renderDeviceLocations(this.locationRows); return }
      const preserve = reconnect && this._locationKey === this.locationBinding
      this.stopLocationFeed()
      if (!preserve) { this.locationRows = []; this.renderDeviceLocations([]) }
      this.locationError = ''
      const host = chartHost(this)
      if (!this._mapDiagnostic) this._mapDiagnostic = registerMapDiagnostic(host, this.id, () => this.retryLocations())
      if (isStaticDataPreview(host) || host.isTemplateEditing || host.isComponentTemplateEditing || host.isTemplatePreview) {
        this.locationStatus = 'static'; this._mapDiagnostic.update({ status: 'static', error: '', count: 0 }); return
      }
      const parameter = this.parameter || {}, pageDevice = parameter.scope === 'device'
      const pageId = host.menuFlag && !host.isPreview ? parameter[pageDevice ? 'previewDeviceId' : 'previewProductId'] : (host.navigationContext || {})[pageDevice ? 'deviceId' : 'productId']
      if (['current', 'device'].includes(parameter.scope) && (pageId == null || (typeof pageId === 'string' && !pageId.trim()))) {
        // 保留本地底图但不伪造点位，也不能以缺少页面参数为由请求全部设备。
        this.locationStatus = 'unbound'; this._mapDiagnostic.update({ status: 'unbound', error: '', count: 0 }); return
      }
      const sequence = this._locationSequence, binding = this.locationBinding
      const current = () => this._locationMounted && this._locationSequence === sequence && binding === this.locationBinding
      this._locationKey = this.locationBinding
      try {
        const query = locationQuery(this.parameter, host.navigationContext || {}, !!host.menuFlag && !host.isPreview)
        this._locationRequest = createRequestCancellation()
        const options = sharedReadOptions(host, { cancelToken: this._locationRequest.token })
        let stream
        try { stream = readonlyDeviceStream(host, `map-${this._uid}-${Date.now()}-${sequence}`, MAP_LOCATION_TOPIC, query) }
        catch (error) { stream = { subscribe (next, failed) { failed(error); return { unsubscribe () {} } } } }
        this._locationFeed = createLocationFeed({ query, stream,
          snapshot: async () => {
            const response = await queryDeviceLocations(query, options), body = response && response.data
            if (!body || body.success === false || (body.status && body.status !== 200) || !body.result) {
              const error = new Error(body && body.message || '地图位置读取失败')
              error.status = body && body.status
              throw error
            }
            return body.result
          },
          changed: (rows, change) => { if (current()) { this.locationRows = rows; this.renderDeviceLocations(rows, change); this._mapDiagnostic.update({ count: rows.length }) } },
          status: (state, error, cause) => {
            if (!current()) return
            this.locationStatus = state; this.locationError = error || ''
            this._mapDiagnostic.update({ status: state, error: error || '', count: this.locationRows.length })
            if (state === 'ready') this._locationAttempts = 0
            if (state === 'error' && ![400, 401, 403, 413].includes(cause && cause.status)) {
              const delay = Math.min(30000, 1000 * Math.pow(2, this._locationAttempts || 0))
              this._locationAttempts = (this._locationAttempts || 0) + 1
              clearTimeout(this._locationRetry)
              this._locationRetry = setTimeout(() => { if (current()) this.updateLocationFeed(true) }, delay)
            }
          }
        })
      } catch (error) { this.locationStatus = 'error'; this.locationError = error.message || '地图设备位置读取失败'; this._mapDiagnostic.update({ status: 'error', error: this.locationError }) }
    }
  }
}
